import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { useInfiniteChatMessages } from '../../../api/hooks'
import { useChatSse } from '../../../sse'
import { chatKeys } from '../../../keys'
import type { PendingMessage } from '../types'

export function useChatMessages(roomId: string) {
	const [pendingMessages, setPendingMessages] = useState<PendingMessage[]>([])
	const {
		onMessageConfirmed,
		jumpToMessageId: startCursor,
		setJumpToMessageId,
	} = useChatSse()
	const cleanupTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map())
	const queryClient = useQueryClient()
	const [isInitialFetch, setIsInitialFetch] = useState(false)
	// Queue for pending messages when viewing older messages (hasPreviousPage)
	// These will be added after resetToLatest completes
	const queuedPendingMessagesRef = useRef<PendingMessage[]>([])

	const {
		data: messagesData,
		isLoading: messagesLoading,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		fetchPreviousPage,
		hasPreviousPage,
		isFetchingPreviousPage,
		isFetched: queryIsFetched,
	} = useInfiniteChatMessages(roomId, startCursor)

	useEffect(() => {
		if (!queryIsFetched) return

		if (startCursor && hasPreviousPage) {
			fetchPreviousPage().then(() => {
				setIsInitialFetch(true)
			})
		} else if (!startCursor && hasPreviousPage) {
			fetchPreviousPage().then(() => {
				setIsInitialFetch(true)
			})
		} else {
			setIsInitialFetch(true)
			// Process queued pending messages after fetch completes (from resetToLatest)
			if (queuedPendingMessagesRef.current.length > 0) {
				const queued = queuedPendingMessagesRef.current
				queuedPendingMessagesRef.current = []
				setPendingMessages((prev) => [...prev, ...queued])
			}
		}
	}, [startCursor, queryIsFetched, hasPreviousPage, fetchPreviousPage])

	useEffect(() => {
		if (!queryIsFetched) {
			setIsInitialFetch(false)
		}
	}, [queryIsFetched])

	// Flatten pages into a single array of messages (reversed for chat - newest at bottom)
	const serverMessages = useMemo(
		() => messagesData?.pages.flatMap((page) => page.items).reverse() ?? [],
		[messagesData],
	)

	// Get set of confirmed clientIds from server messages
	const confirmedClientIds = useMemo(
		() =>
			new Set(
				serverMessages
					.filter((m) => m.clientId)
					.map((m) => m.clientId as string),
			),
		[serverMessages],
	)

	// Combine server messages with pending messages (that aren't confirmed yet)
	// Failed messages are always shown at the end of the list
	const messages = useMemo(() => {
		const activePendingMessages = pendingMessages.filter(
			(pm) => !confirmedClientIds.has(pm.clientId),
		)

		// Separate pending and failed messages
		const pendingOnly = activePendingMessages
			.filter((pm) => pm.status !== 'failed')
			.map((pm) => pm.message)
		const failedOnly = activePendingMessages
			.filter((pm) => pm.status === 'failed')
			.map((pm) => pm.message)

		// Failed messages always at the end
		return [...serverMessages, ...pendingOnly, ...failedOnly]
	}, [serverMessages, pendingMessages, confirmedClientIds])

	// Listen to message_confirmed SSE events - just update status
	useEffect(() => {
		const unsubscribe = onMessageConfirmed((payload) => {
			if (payload.roomId !== roomId) return

			// Mark pending message as sent
			setPendingMessages((prev) =>
				prev.map((pm) =>
					pm.clientId === payload.clientId ? { ...pm, status: 'sent' } : pm,
				),
			)
		})

		return unsubscribe
	}, [onMessageConfirmed, roomId])

	// Clean up pending messages when they appear in server messages
	useEffect(() => {
		pendingMessages.forEach((pm) => {
			if (confirmedClientIds.has(pm.clientId)) {
				// Already have a cleanup scheduled? Skip
				if (cleanupTimeoutsRef.current.has(pm.clientId)) return

				// Schedule cleanup
				const timeout = setTimeout(() => {
					setPendingMessages((prev) =>
						prev.filter((p) => p.clientId !== pm.clientId),
					)
					cleanupTimeoutsRef.current.delete(pm.clientId)
				}, 500)

				cleanupTimeoutsRef.current.set(pm.clientId, timeout)
			}
		})
	}, [confirmedClientIds, pendingMessages])

	// Reset pending messages and cleanup timeouts when room changes
	useEffect(() => {
		setPendingMessages([])
		// Clear all pending timeouts
		cleanupTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout))
		cleanupTimeoutsRef.current.clear()
	}, [roomId])

	// Cleanup timeouts and clear message cache on unmount
	useEffect(() => {
		const timeoutsMap = cleanupTimeoutsRef.current
		return () => {
			timeoutsMap.forEach((timeout) => clearTimeout(timeout))
			timeoutsMap.clear()
			// Clear message query cache when leaving the room
			queryClient.removeQueries({
				queryKey: chatKeys.messages(roomId),
			})
		}
	}, [queryClient, roomId])

	// Add a pending message directly (without reset check)
	const addPendingMessage = useCallback((message: PendingMessage) => {
		setPendingMessages((prev) => [...prev, message])
	}, [])

	// Add a pending message with auto-reset to latest if viewing older messages
	// Returns true if reset was triggered (caller should wait for fetch)
	const addPendingMessageWithReset = useCallback(
		(message: PendingMessage): boolean => {
			if (hasPreviousPage) {
				// Queue the message to be added after reset completes
				queuedPendingMessagesRef.current.push(message)
				// Reset to latest messages
				if (startCursor) {
					setJumpToMessageId(null)
				}
				queryClient.invalidateQueries({
					queryKey: chatKeys.messages(roomId),
				})
				return true // Reset triggered
			}
			// Normal case: just add the message
			setPendingMessages((prev) => [...prev, message])
			return false // No reset needed
		},
		[hasPreviousPage, startCursor, setJumpToMessageId, queryClient, roomId],
	)

	// Mark a pending message as failed
	const markPendingAsFailed = useCallback((clientId: string) => {
		setPendingMessages((prev) =>
			prev.map((pm) =>
				pm.clientId === clientId ? { ...pm, status: 'failed' } : pm,
			),
		)
	}, [])

	// Remove a failed message
	const removeFailedMessage = useCallback((clientId: string) => {
		setPendingMessages((prev) => prev.filter((pm) => pm.clientId !== clientId))
	}, [])

	// Reset a failed message to pending for retry
	const retryMessage = useCallback((clientId: string) => {
		setPendingMessages((prev) =>
			prev.map((pm) =>
				pm.clientId === clientId ? { ...pm, status: 'pending' } : pm,
			),
		)
	}, [])

	// Get pending info for a message
	const getPendingInfo = useCallback(
		(clientId?: string) => {
			if (!clientId) return undefined
			return pendingMessages.find((pm) => pm.clientId === clientId)
		},
		[pendingMessages],
	)

	// Get pending message data for retry
	const getFailedMessageData = useCallback(
		(clientId: string) => {
			return pendingMessages.find(
				(pm) => pm.clientId === clientId && pm.status === 'failed',
			)
		},
		[pendingMessages],
	)

	// Jump to a specific message by setting context value
	// This will trigger React Query to refetch with the new startCursor
	const jumpToMessage = useCallback(
		async (messageId: string) => {
			// Set messageId in context - this changes the query key
			// and triggers React Query to refetch with the new startCursor
			setJumpToMessageId(messageId)
			// Invalidate old query cache to ensure clean state
			queryClient.invalidateQueries({
				queryKey: chatKeys.messages(roomId),
			})
		},
		[roomId, queryClient, setJumpToMessageId],
	)

	// Reset to latest messages - clear context and invalidate query
	// Used when scrolling to bottom but we're viewing older messages
	const resetToLatest = useCallback(() => {
		// Clear messageId in context if exists
		if (startCursor) {
			setJumpToMessageId(null)
		}
		// Invalidate query to refetch from latest
		queryClient.invalidateQueries({
			queryKey: chatKeys.messages(roomId),
		})
	}, [startCursor, setJumpToMessageId, queryClient, roomId])

	return {
		messages,
		serverMessages,
		pendingMessages,
		messagesLoading,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		fetchPreviousPage,
		hasPreviousPage,
		isFetchingPreviousPage,
		addPendingMessage,
		addPendingMessageWithReset,
		markPendingAsFailed,
		removeFailedMessage,
		retryMessage,
		getPendingInfo,
		getFailedMessageData,
		jumpToMessage,
		resetToLatest,
		startCursor,
		isFetched: queryIsFetched && isInitialFetch,
	}
}
